export interface ApiSample {
  ts: number;
  tenantId: string;
  method: string;
  path: string;
  durationMs: number;
  status: number;
  ok: boolean;
}

export interface SyncSample {
  ts: number;
  tenantId: string;
  success: boolean;
}

interface StoredUser {
  tenantId?: string;
}

const API_KEY = 'petronexus-observability-api';
const SYNC_KEY = 'petronexus-observability-sync';
const MAX_API_SAMPLES = 1200;
const MAX_SYNC_SAMPLES = 600;

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function getCurrentTenantId() {
  const user = safeParse<StoredUser | null>(localStorage.getItem('user'), null);
  return user?.tenantId ?? 'unknown';
}

function appendWithCap<T>(arr: T[], sample: T, max: number) {
  const next = [...arr, sample];
  return next.length > max ? next.slice(next.length - max) : next;
}

export function recordApiSample(sample: Omit<ApiSample, 'tenantId' | 'ts'>) {
  const item: ApiSample = {
    ...sample,
    ts: Date.now(),
    tenantId: getCurrentTenantId(),
  };
  const existing = safeParse<ApiSample[]>(localStorage.getItem(API_KEY), []);
  localStorage.setItem(API_KEY, JSON.stringify(appendWithCap(existing, item, MAX_API_SAMPLES)));
}

export function recordSyncSample(success: boolean) {
  const item: SyncSample = {
    ts: Date.now(),
    tenantId: getCurrentTenantId(),
    success,
  };
  const existing = safeParse<SyncSample[]>(localStorage.getItem(SYNC_KEY), []);
  localStorage.setItem(SYNC_KEY, JSON.stringify(appendWithCap(existing, item, MAX_SYNC_SAMPLES)));
}

export function getApiSamples(windowHours = 24): ApiSample[] {
  const cutoff = Date.now() - windowHours * 60 * 60 * 1000;
  return safeParse<ApiSample[]>(localStorage.getItem(API_KEY), []).filter((s) => s.ts >= cutoff);
}

export function getSyncSamples(windowHours = 24): SyncSample[] {
  const cutoff = Date.now() - windowHours * 60 * 60 * 1000;
  return safeParse<SyncSample[]>(localStorage.getItem(SYNC_KEY), []).filter((s) => s.ts >= cutoff);
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, Math.min(idx, sorted.length - 1))];
}

export function getSloSnapshot(windowHours = 24, latencyTargetMs = 500, errorBudget = 0.01) {
  const api = getApiSamples(windowHours);
  const sync = getSyncSamples(windowHours);

  const totalReq = api.length;
  const failedReq = api.filter((s) => !s.ok).length;
  const durations = api.map((s) => s.durationMs);
  const p50 = percentile(durations, 50);
  const p95 = percentile(durations, 95);
  const goodLatencyReq = api.filter((s) => s.ok && s.durationMs <= latencyTargetMs).length;

  const errorRate = totalReq > 0 ? failedReq / totalReq : 0;
  const latencySlo = totalReq > 0 ? goodLatencyReq / totalReq : 0;
  const budgetUsedPct = errorBudget > 0 ? Math.min(100, (errorRate / errorBudget) * 100) : 0;

  const syncFailures = sync.filter((s) => !s.success).length;
  const syncFailureRate = sync.length > 0 ? syncFailures / sync.length : 0;

  const perPath = Object.values(
    api.reduce<Record<string, { path: string; count: number; p95: number; errorRate: number }>>((acc, row) => {
      const key = `${row.method} ${row.path}`;
      if (!acc[key]) {
        acc[key] = { path: key, count: 0, p95: 0, errorRate: 0 };
      }
      acc[key].count += 1;
      return acc;
    }, {}),
  ).map((entry) => {
    const rows = api.filter((a) => `${a.method} ${a.path}` === entry.path);
    const rowErrors = rows.filter((r) => !r.ok).length;
    return {
      ...entry,
      p95: percentile(rows.map((r) => r.durationMs), 95),
      errorRate: rows.length > 0 ? rowErrors / rows.length : 0,
    };
  }).sort((a, b) => b.count - a.count);

  const perTenant = Object.values(
    api.reduce<Record<string, { tenantId: string; requests: number; p95: number; errorRate: number }>>((acc, row) => {
      if (!acc[row.tenantId]) {
        acc[row.tenantId] = { tenantId: row.tenantId, requests: 0, p95: 0, errorRate: 0 };
      }
      acc[row.tenantId].requests += 1;
      return acc;
    }, {}),
  ).map((entry) => {
    const rows = api.filter((a) => a.tenantId === entry.tenantId);
    const rowErrors = rows.filter((r) => !r.ok).length;
    return {
      ...entry,
      p95: percentile(rows.map((r) => r.durationMs), 95),
      errorRate: rows.length > 0 ? rowErrors / rows.length : 0,
    };
  });

  return {
    totalReq,
    failedReq,
    p50,
    p95,
    errorRate,
    latencySlo,
    budgetUsedPct,
    syncFailureRate,
    syncAttempts: sync.length,
    perPath,
    perTenant,
  };
}
